import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { AccountService } from 'src/app/core/services/account.service';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { OneFiService } from 'src/app/core/services/onefi.service';

@Component({
  selector: 'app-onefi-callback',
  templateUrl: './callback.component.html',
  styleUrls: ['./callback.component.scss']
})
export class CallbackComponent implements OnInit, OnDestroy {
  isChecking: boolean = true;
  isSuccess: boolean = false;
  isExpired: boolean = false;
  errorMessage: string = 'Your application process was not completed.';
  statusMessage: string = 'Verifying your application with 1Fi...';

  transactionId: string = '';
  attemptCount: number = 0;
  maxAttempts: number = 5;

  private pollTimer: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private oneFiService: OneFiService,
    private accountService: AccountService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.spinner.hide();
    this.verifyStatus();
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.spinner.hide();
  }

  private clearTimer(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async verifyStatus(): Promise<void> {
    this.isChecking = true;
    this.isSuccess = false;
    this.isExpired = false;
    this.attemptCount = 0;
    this.statusMessage = 'Verifying your application with 1Fi...';
    this.cdr.detectChanges();

    // 1. Check Query Params from return URL
    const queryParams = this.route.snapshot.queryParams;
    let txId = queryParams['transaction_id'] || queryParams['transactionId'] || queryParams['id'];

    // 2. Fallback to Memory / Storage
    if (!txId) {
      txId = MemoryStorageService.getItem('oneFiTransactionId') 
        || MemoryStorageService.getItem('lenderApplId')
        || localStorage.getItem('oneFiTransactionId');
    }

    // 3. Fallback to Navigator
    if (!txId) {
      try {
        const navRes: any = await lastValueFrom(this.accountService.getNavigator());
        if (navRes?.data?.applicationDetail?.lenderApplId) {
          txId = navRes.data.applicationDetail.lenderApplId;
        }
      } catch (navErr) {
        console.warn('Could not fetch transaction ID from navigator:', navErr);
      }
    }

    this.transactionId = txId || '';
    if (this.transactionId) {
      MemoryStorageService.setItem('oneFiTransactionId', this.transactionId);
      MemoryStorageService.setItem('lenderApplId', this.transactionId);
      localStorage.setItem('oneFiTransactionId', this.transactionId);
    }

    this.pollStatus();
  }

  async pollStatus(): Promise<void> {
    this.attemptCount++;
    this.statusMessage = 'Checking completion status with 1Fi...';
    this.cdr.detectChanges();

    try {
      const res: any = await lastValueFrom(this.oneFiService.checkTransactionStatus(this.transactionId));
      const data = res?.data;
      const isComplete = data?.is_completed === true 
        || data?.all_complete === true 
        || (data?.details?.order_confirmed === true && data?.details?.loan_disbursed === true);

      if (isComplete) {
        this.handleSuccess();
        return;
      }

      if (data?.status === 'Session Expired' || data?.status === 'EXPIRED' || data?.status === 'Rejected') {
        this.handleExpired('Your application session has expired or was not completed.');
        return;
      }

      if (this.attemptCount < this.maxAttempts) {
        this.pollTimer = setTimeout(() => {
          this.ngZone.run(() => {
            this.pollStatus();
          });
        }, 2000);
      } else {
        // Max attempts reached without completion -> session expired
        this.handleExpired('Your application process was not completed on 1Fi.');
      }
    } catch (err: any) {
      console.warn('Error checking 1Fi status:', err);
      if (this.attemptCount < this.maxAttempts) {
        this.pollTimer = setTimeout(() => {
          this.ngZone.run(() => {
            this.pollStatus();
          });
        }, 2500);
      } else {
        this.handleExpired('Unable to verify application status. Session expired.');
      }
    }
  }

  private handleSuccess(): void {
    this.ngZone.run(() => {
      this.clearTimer();
      this.isChecking = false;
      this.isSuccess = true;
      this.cdr.detectChanges();

      setTimeout(() => {
        this.ngZone.run(() => {
          this.router.navigate(['/assist-journey/assist-success']);
        });
      }, 1200);
    });
  }

  private handleExpired(message: string): void {
    this.ngZone.run(() => {
      this.clearTimer();
      this.isChecking = false;
      this.isExpired = true;
      this.errorMessage = message;
      this.cdr.detectChanges();

      // Automatically navigate to session-expired after 3 seconds
      setTimeout(() => {
        this.ngZone.run(() => {
          this.goToSessionExpired();
        });
      }, 3000);
    });
  }

  goToSessionExpired(): void {
    this.clearTimer();
    this.router.navigate(['/assist-journey/session-expired']);
  }
}
